import type Phaser from 'phaser';
import { applyTurn, Dir, dx, dy, type Direction } from '../../sim/direction';
import type { SimEvent } from '../../sim/events';
import type { SteerIntent } from '../../sim/mower';
import type { Simulation, TurnPreview } from '../../sim/simulation';
import { turnRuleIndicators } from '../../sim/turnRules';
import { TILE_SIZE } from '../layout';
import { MOWER_DEFS, type MowerId } from '../../content/mowers';

/**
 * Makes steering legible (playtest feedback):
 * - an arrow on the tile where a buffered turn will happen, colored by whether it will work;
 * - a short "ghost" arrow where a turn just happened, and a red cross when a request expires;
 * - the reel's pull-back charge bar and the tractor's turning-radius lock.
 */

const COLORS: Record<TurnPreview['outcome'], number> = {
  ok: 0xffe066,
  blocked: 0xff9f43,
  rule: 0xff5a4a,
  // Lands on a wet (sprinkled) tile: the mower slides on and turns at the next dry one.
  wet: 0x7cc6ff,
};
const OUTLINE = 0x1b2616;
const LOCK = 0xff5a4a;
const CHARGE_BG = 0x1b2616;
const CHARGE_FILL = 0x8fd3ff;

/** 7×7 arrow pointing north; '#' = fill. Rotated for other headings. */
const ARROW = ['...#...', '..###..', '.#####.', '...#...', '...#...', '...#...', '.......'];
const CROSS = ['#...#', '.#.#.', '..#..', '.#.#.', '#...#'];
/** 9×9 chunky arrow pointing north: the tractor's "not this way yet" sign. */
const LOCK_ARROW = [
  '....#....',
  '...###...',
  '..#####..',
  '.#######.',
  '...###...',
  '...###...',
  '...###...',
];
/** 5-wide arrowhead pointing north. */
const HEAD = ['..#..', '.###.', '#####', '.....', '.....'];

function rotate(px: number, py: number, size: number, dir: Direction): [number, number] {
  const last = size - 1;
  switch (dir) {
    case Dir.N:
      return [px, py];
    case Dir.E:
      return [last - py, px];
    case Dir.S:
      return [last - px, last - py];
    case Dir.W:
      return [py, last - px];
  }
}

interface Flash {
  kind: 'ghost' | 'cross';
  x: number;
  y: number;
  dir: Direction;
  until: number;
}

export class TurnHints {
  private readonly below: Phaser.GameObjects.Graphics;
  private readonly above: Phaser.GameObjects.Graphics;
  private readonly flashes: Flash[] = [];
  /**
   * Whether to show the tractor's turning lock: not once Power Steering has eased it (owner,
   * 2026-10-07). A loaner (the Daily) drives stock, so it still shows there.
   */
  private readonly showLock: boolean;

  constructor(
    private readonly scene: Phaser.Scene,
    private sim: Simulation,
    mowerDepth: number,
  ) {
    this.below = scene.add.graphics().setDepth(mowerDepth - 1);
    this.above = scene.add.graphics().setDepth(mowerDepth + 1);
    const rule = sim.mowerSpec.turnRule;
    const stock = MOWER_DEFS[sim.mowerSpec.id as MowerId]?.turnRule;
    this.showLock = !(
      rule.kind === 'turnSpacing' &&
      stock?.kind === 'turnSpacing' &&
      rule.sameDirectionMinTiles < stock.sameDirectionMinTiles
    );
  }

  /** Reads a new copy of the match from now on (head to head rebuilds it on a late turn). */
  resync(sim: Simulation): void {
    this.sim = sim;
    this.flashes.length = 0;
  }

  applyEvents(events: readonly SimEvent[]): void {
    const now = this.scene.time.now;
    const m = this.sim.mower;
    for (const e of events) {
      if (e.type === 'turned') {
        this.flashes.push({ kind: 'ghost', x: e.x, y: e.y, dir: e.dir, until: now + 220 });
      } else if (e.type === 'turnRejected') {
        this.flashes.push({ kind: 'cross', x: m.x, y: m.y, dir: m.dir, until: now + 400 });
      }
    }
  }

  /**
   * A steering request to preview instead of the sim's pending one: a turn picked during the
   * resume countdown, before it is sent to the sim.
   */
  previewIntent: SteerIntent | null = null;

  /** Redraws hints; `mowerPx/Py` is the interpolated mower center in world pixels. */
  render(mowerPx: number, mowerPy: number): void {
    const now = this.scene.time.now;
    this.below.clear();
    this.above.clear();
    const running = this.sim.status === 'running';

    // Pending turn arrow, pulsing so it reads as "about to happen".
    const preview = running
      ? this.sim.pendingTurnPreview(this.previewIntent ?? this.sim.mower.pendingIntent)
      : null;
    if (preview) {
      const pulse = 0.65 + 0.35 * Math.abs(Math.sin(now / 140));
      this.drawPath(mowerPx, mowerPy, preview, COLORS[preview.outcome], pulse);
    }

    // Recent turn ghosts and expired-request crosses.
    for (let i = this.flashes.length - 1; i >= 0; i--) {
      const f = this.flashes[i] as Flash;
      if (now >= f.until) {
        this.flashes.splice(i, 1);
        continue;
      }
      const alpha = Math.min(1, (f.until - now) / 150);
      if (f.kind === 'ghost')
        this.drawPattern(this.below, ARROW, f.x, f.y, f.dir, 0xffffff, 0.5 * alpha);
      else this.drawPattern(this.above, CROSS, f.x, f.y, Dir.N, COLORS.rule, alpha);
    }

    if (!running) return;
    const m = this.sim.mower;
    const ind = turnRuleIndicators(this.sim.mowerSpec.turnRule, m);

    // Reel: pull-back charge bar under the mower while recharging.
    if (ind.pullbackCharge !== null && ind.pullbackCharge < 1) {
      const w = 14;
      const x = Math.round(mowerPx - w / 2);
      const y = Math.round(mowerPy + TILE_SIZE / 2);
      this.above.fillStyle(CHARGE_BG, 0.95).fillRect(x - 1, y - 1, w + 2, 5);
      this.above.fillStyle(CHARGE_FILL, 1).fillRect(x, y, Math.round(w * ind.pullbackCharge), 3);
    }

    // Tractor (owner, 2026-10-07: say "you can't turn this way until there"): beside the mower, a
    // "no turn" sign (the turn's arrow in a red ring, struck through); on the tile where that turn
    // opens again, a chunky arrow in the hints' "this turn works" yellow, nudging that way: turn
    // from here. (Solid, not fading: a see-through yellow goes olive on the grass.)
    if (ind.lockedTurn && this.showLock) {
      const side = applyTurn(m.dir, ind.lockedTurn);
      const cx = Math.round(mowerPx + dx(side) * 17);
      const cy = Math.round(mowerPy + dy(side) * 17);
      this.drawNoTurn(cx, cy, side);

      const ahead = Math.max(ind.lockTilesLeft, m.progress > 0 ? 1 : 0);
      const ux = m.x + dx(m.dir) * ahead;
      const uy = m.y + dy(m.dir) * ahead;
      if (this.sim.grid.inBounds(ux, uy)) {
        const half = TILE_SIZE / 2;
        const nudge = 3 + (Math.floor(now / 260) % 2);
        this.drawPatternAt(
          this.below,
          LOCK_ARROW,
          ux * TILE_SIZE + half + dx(side) * nudge,
          uy * TILE_SIZE + half + dy(side) * nudge,
          side,
          COLORS.ok,
          1,
        );
      }
    }
  }

  /**
   * The upcoming bend: a line from the mower to the turn tile's center, then on in the new heading,
   * ending in an arrowhead. Shows exactly where the mower will go. The arrowhead is drawn over the
   * mower, and for the Fairway Mower the new heading's leg runs on past its side reels (which lie
   * along it once it has turned), so the arrow never hides under a big mower.
   */
  private drawPath(
    fromX: number,
    fromY: number,
    preview: TurnPreview,
    color: number,
    alpha: number,
  ): void {
    const g = this.below;
    const half = TILE_SIZE / 2;
    const cx = preview.x * TILE_SIZE + half;
    const cy = preview.y * TILE_SIZE + half;
    const leg = this.sim.mowerSpec.wings ? TILE_SIZE * 2 : TILE_SIZE - 4;
    const ex = cx + dx(preview.dir) * leg;
    const ey = cy + dy(preview.dir) * leg;
    const segments: [number, number, number, number][] = [
      [Math.round(fromX), Math.round(fromY), cx, cy],
      [cx, cy, ex, ey],
    ];
    const rect = (x0: number, y0: number, x1: number, y1: number, pad: number) =>
      g.fillRect(
        Math.min(x0, x1) - pad,
        Math.min(y0, y1) - pad,
        Math.abs(x1 - x0) + 2 * pad,
        Math.abs(y1 - y0) + 2 * pad,
      );
    g.fillStyle(OUTLINE, alpha * 0.7);
    for (const [x0, y0, x1, y1] of segments) rect(x0, y0, x1, y1, 2);
    g.fillStyle(color, alpha);
    for (const [x0, y0, x1, y1] of segments) rect(x0, y0, x1, y1, 1);

    // Arrowhead whose tip sits just past the end of the line.
    const cells: [number, number][] = [];
    HEAD.forEach((row, py) =>
      [...row].forEach((ch, px) => {
        if (ch === '#') cells.push(rotate(px, py, 5, preview.dir));
      }),
    );
    const hx = ex - 2 + dx(preview.dir) * 2;
    const hy = ey - 2 + dy(preview.dir) * 2;
    const head = this.above;
    head.fillStyle(OUTLINE, alpha * 0.8);
    for (const [x, y] of cells) head.fillRect(hx + x - 1, hy + y - 1, 3, 3);
    head.fillStyle(color, alpha);
    for (const [x, y] of cells) head.fillRect(hx + x, hy + y, 1, 1);
  }

  /** Four corner ticks outlining a tile: "the locked turn becomes available here". */
  /** A "no turn" road sign at (cx, cy): a red ring, the turn's arrow inside, a red slash across. */
  private drawNoTurn(cx: number, cy: number, side: Direction): void {
    const g = this.above;
    g.fillStyle(OUTLINE, 0.95).fillCircle(cx, cy, 10);
    g.fillStyle(LOCK, 1).fillCircle(cx, cy, 9);
    g.fillStyle(0xfffaf0, 1).fillCircle(cx, cy, 7);
    // The slash first (top left to bottom right), the arrow over it in plain ink pixels.
    g.fillStyle(LOCK, 1);
    for (let k = -6; k <= 5; k++) g.fillRect(cx + k, cy + k, 2, 2);
    const size = ARROW.length;
    ARROW.forEach((row, py) =>
      [...row].forEach((ch, px) => {
        if (ch !== '#') return;
        const [rx, ry] = rotate(px, py, size, side);
        g.fillStyle(OUTLINE, 1).fillRect(cx - 3 + rx, cy - 3 + ry, 1, 1);
      }),
    );
  }

  /**
   * Draws a pixel pattern centered on tile (tx, ty), rotated to `dir`, with a dark outline.
   */
  private drawPattern(
    g: Phaser.GameObjects.Graphics,
    pattern: readonly string[],
    tx: number,
    ty: number,
    dir: Direction,
    color: number,
    alpha: number,
  ): void {
    const half = TILE_SIZE / 2;
    this.drawPatternAt(g, pattern, tx * TILE_SIZE + half, ty * TILE_SIZE + half, dir, color, alpha);
  }

  /** A pattern centered on (cx, cy) in world pixels, turned to face `dir`, with an outline. */
  private drawPatternAt(
    g: Phaser.GameObjects.Graphics,
    pattern: readonly string[],
    cx: number,
    cy: number,
    dir: Direction,
    color: number,
    alpha: number,
  ): void {
    const size = Math.max(pattern.length, pattern[0]?.length ?? 0);
    const ox = cx - TILE_SIZE / 2 + Math.floor((TILE_SIZE - size) / 2);
    const oy = cy - TILE_SIZE / 2 + Math.floor((TILE_SIZE - size) / 2);
    const cells: [number, number][] = [];
    pattern.forEach((row, py) => {
      [...row].forEach((ch, px) => {
        if (ch !== '#') return;
        cells.push(rotate(px, py, size, dir));
      });
    });
    g.fillStyle(OUTLINE, alpha * 0.8);
    for (const [x, y] of cells) g.fillRect(ox + x - 1, oy + y - 1, 3, 3);
    g.fillStyle(color, alpha);
    for (const [x, y] of cells) g.fillRect(ox + x, oy + y, 1, 1);
  }
}
